import React, { useState } from 'react';
import { Pencil, FileText, Search, Eye, ClipboardList, Users, Link, BarChart2 } from 'lucide-react';
import { Button, Modal, EmptyState, Badge, RespondersModal } from '../components/common';
import type { SurveyTemplate, UpdateTemplatePayload, SurveyQuestion, Survey, Organization } from '../types';
import { getTemplate, updateQuestion, getRespondersByTemplate, getRespondersByInstance } from '../services/api';
import { copyRegisterLink } from '../utils/clipboard';

interface TemplatesPageProps {
    templates: SurveyTemplate[];
    instances: Survey[];
    organizations: Organization[];
    loading: boolean;
    onUpdateName: (payload: UpdateTemplatePayload) => Promise<void>;
    onShowToast: (type: 'success' | 'error' | 'warning' | 'info', title: string, message?: string) => void;
    onNavigateToInstanceAnalytics?: (instanceId: string) => void;
    onNavigateToRespondent?: (respondentId: string, context: { templateId?: number; instanceId?: string }) => void;
}

export const TemplatesPage: React.FC<TemplatesPageProps> = ({
    templates,
    instances,
    organizations,
    loading,
    onUpdateName,
    onShowToast,
    onNavigateToInstanceAnalytics,
    onNavigateToRespondent,
}: TemplatesPageProps) => {
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [editingTemplate, setEditingTemplate] = useState<SurveyTemplate | null>(null);
    const [viewingQuestions, setViewingQuestions] = useState<SurveyQuestion[]>([]);
    const [viewModalLoading, setViewModalLoading] = useState(false);
    const [newName, setNewName] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [editingQuestion, setEditingQuestion] = useState<SurveyQuestion | null>(null);
    const [isQuestionModalOpen, setIsQuestionModalOpen] = useState(false);
    const [questionText, setQuestionText] = useState('');
    const [isInstancesModalOpen, setIsInstancesModalOpen] = useState(false);
    const [isRespondersModalOpen, setIsRespondersModalOpen] = useState(false);
    const [isInstanceRespondersModalOpen, setIsInstanceRespondersModalOpen] = useState(false);
    const [viewingInstance, setViewingInstance] = useState<Survey | null>(null);

    const filteredTemplates = templates.filter((t: SurveyTemplate) =>
        t.name?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const openEditModal = (template: SurveyTemplate) => {
        setEditingTemplate(template);
        setNewName(template.name || '');
        setIsEditModalOpen(true);
    };

    const handleViewQuestions = async (template: SurveyTemplate) => {
        setEditingTemplate(template);
        setIsViewModalOpen(true);
        setViewModalLoading(true);
        try {
            const templateDetail = await getTemplate(template.id);
            const questions = templateDetail.questions || [];
            setViewingQuestions(questions.sort((a, b) => (a.order_index || 0) - (b.order_index || 0)));
        } catch (error) {
            console.error('Failed to load questions', error);
        } finally {
            setViewModalLoading(false);
        }
    };

    const handleUpdateName = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!editingTemplate) return;

        setSubmitting(true);
        try {
            await onUpdateName({
                template_id: editingTemplate.id,
                name: newName,
            });
            setIsEditModalOpen(false);
        } finally {
            setSubmitting(false);
        }
    };

    const handleEditQuestion = (question: SurveyQuestion) => {
        setEditingQuestion(question);
        setQuestionText(question.text_he);
        setIsQuestionModalOpen(true);
    };

    const handleSaveQuestion = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!editingQuestion) return;

        setSubmitting(true);
        try {
            await updateQuestion({
                question_id: editingQuestion.id,
                text_he: questionText,
            });

            setViewingQuestions(prev =>
                prev.map(q => q.id === editingQuestion.id ? { ...q, text_he: questionText } : q)
            );

            setIsQuestionModalOpen(false);
        } catch (error) {
            console.error('Failed to update question', error);
        } finally {
            setSubmitting(false);
        }
    };

    const handleViewInstances = (template: SurveyTemplate) => {
        setEditingTemplate(template);
        setIsInstancesModalOpen(true);
    };

    const getTemplateInstances = (templateId: number) => {
        return instances.filter(i => i.template_id === templateId);
    };

    const getOrgName = (orgId: string) => {
        const org = organizations.find(o => o.id === orgId);
        return org?.name || 'ארגון לא ידוע';
    };

    const handleCopyRegisterLink = (instanceId: string) => copyRegisterLink(instanceId, onShowToast);


    return (
        <div>
            {/* Header Actions */}
            <div className="page-header-actions">
                <div className="search-wrapper">
                    <Search
                        size={18}
                        className="search-icon"
                    />
                    <input
                        type="text"
                        placeholder="חיפוש תבניות..."
                        value={searchQuery}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
                    />
                </div>
            </div>

            {/* Templates Table */}
            <div className="card">
                {loading ? (
                    <div className="loading-overlay">
                        <div className="spinner" />
                        <span className="loading-text">טוען תבניות...</span>
                    </div>
                ) : templates.length === 0 ? (
                    <EmptyState
                        icon={<FileText size={64} />}
                        title="אין תבניות עדיין"
                        description="לא נמצאו תבניות במערכת"
                    />
                ) : (
                    <div className="table-container">
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th>שם התבנית</th>
                                    <th>תאריך יצירה</th>
                                    <th>פעולות</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredTemplates.map((template: SurveyTemplate) => (
                                    <tr key={template.id}>
                                        <td data-label="שם התבנית">
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                                                <div
                                                    style={{
                                                        width: 36,
                                                        height: 36,
                                                        borderRadius: 'var(--radius-lg)',
                                                        background: 'linear-gradient(135deg, var(--info-100), var(--info-200))',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        color: 'var(--info-600)',
                                                    }}
                                                >
                                                    <FileText size={18} />
                                                </div>
                                                <span style={{ fontWeight: 500 }}>{template.name || 'ללא שם'}</span>
                                            </div>
                                        </td>
                                        <td data-label="תאריך יצירה" style={{ color: 'var(--neutral-500)' }}>
                                            {new Date(template.created_at).toLocaleDateString('he-IL')}
                                        </td>
                                        <td data-label="פעולות">
                                            <div className="cell-actions">
                                                <button
                                                    className="btn btn-icon btn-ghost sm"
                                                    onClick={() => {
                                                        setEditingTemplate(template);
                                                        setIsRespondersModalOpen(true);
                                                    }}
                                                    title="צפה במשיבים"
                                                >
                                                    <Users size={16} />
                                                </button>
                                                <button
                                                    className="btn btn-icon btn-ghost sm"
                                                    onClick={() => handleViewInstances(template)}
                                                    title="צפה בקבוצות סקר"
                                                >
                                                    <ClipboardList size={16} />
                                                </button>
                                                <button
                                                    className="btn btn-icon btn-ghost sm"
                                                    onClick={() => handleViewQuestions(template)}
                                                    title="צפייה בשאלות"
                                                >
                                                    <Eye size={16} />
                                                </button>
                                                <button
                                                    className="btn btn-icon btn-ghost sm"
                                                    onClick={() => openEditModal(template)}
                                                    title="שינוי שם"
                                                >
                                                    <Pencil size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Edit Name Modal */}
            <Modal
                isOpen={isEditModalOpen}
                onClose={() => setIsEditModalOpen(false)}
                title="שינוי שם תבנית"
                footer={
                    <>
                        <Button onClick={handleUpdateName} loading={submitting}>
                            שמור
                        </Button>
                        <Button variant="secondary" onClick={() => setIsEditModalOpen(false)}>
                            ביטול
                        </Button>
                    </>
                }
            >
                <form onSubmit={handleUpdateName}>
                    <div className="form-group">
                        <label>שם התבנית</label>
                        <input
                            type="text"
                            value={newName}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewName(e.target.value)}
                            placeholder="הזן שם לתבנית"
                            required
                        />
                    </div>
                </form>
            </Modal>

            {/* View Questions Modal */}
            <Modal
                isOpen={isViewModalOpen}
                onClose={() => setIsViewModalOpen(false)}
                title={`שאלות בתבנית: ${editingTemplate?.name}`}
                size="large"
            >
                {viewModalLoading ? (
                    <div style={{ padding: 'var(--space-10)', textAlign: 'center' }}>
                        <div className="spinner" style={{ margin: '0 auto var(--space-4)' }} />
                        <span>טוען שאלות...</span>
                    </div>
                ) : viewingQuestions.length === 0 ? (
                    <div style={{ padding: 'var(--space-10)', textAlign: 'center', color: 'var(--neutral-500)' }}>
                        אין שאלות בתבנית זו
                    </div>
                ) : (
                    <div className="table-container" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th>קטגוריה</th>
                                    <th>שאלה</th>
                                    <th style={{ textAlign: 'center' }}>היפוך</th>
                                    <th style={{ textAlign: 'center' }}>פעולות</th>
                                </tr>
                            </thead>
                            <tbody>
                                {viewingQuestions.map((q: SurveyQuestion) => (
                                    <tr key={q.id}>
                                        <td>
                                            <span className="badge badge-neutral">{q.category}</span>
                                        </td>
                                        <td style={{ fontWeight: 500 }}>{q.text_he}</td>
                                        <td style={{ textAlign: 'center' }}>
                                            {q.score_inversion ? (
                                                <span style={{ color: 'var(--warning-500)', fontWeight: 'bold' }}>כן</span>
                                            ) : 'לא'}
                                        </td>
                                        <td style={{ textAlign: 'center' }}>
                                            <button
                                                className="btn btn-icon btn-ghost sm"
                                                onClick={() => handleEditQuestion(q)}
                                                title="ערוך שאלה"
                                            >
                                                <Pencil size={14} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Modal>

            {/* Edit Question Text Modal */}
            <Modal
                isOpen={isQuestionModalOpen}
                onClose={() => setIsQuestionModalOpen(false)}
                title="עריכת טקסט שאלה"
                footer={
                    <>
                        <Button onClick={handleSaveQuestion} loading={submitting}>
                            שמור
                        </Button>
                        <Button variant="secondary" onClick={() => setIsQuestionModalOpen(false)}>
                            ביטול
                        </Button>
                    </>
                }
            >
                <form onSubmit={handleSaveQuestion}>
                    <div className="form-group">
                        <label>טקסט השאלה (בעברית)</label>
                        <textarea
                            value={questionText}
                            onChange={(e) => setQuestionText(e.target.value)}
                            placeholder="הזן את טקסט השאלה"
                            required
                            style={{ width: '100%', minHeight: '100px', padding: 'var(--space-3)', borderRadius: 'var(--radius-md)', border: '1px solid var(--neutral-200)' }}
                        />
                    </div>
                </form>
            </Modal>

            {/* View Instances Modal */}
            <Modal
                isOpen={isInstancesModalOpen}
                onClose={() => setIsInstancesModalOpen(false)}
                title={`קבוצות סקרים לתבנית: ${editingTemplate?.name}`}
                size="large"
            >
                {editingTemplate && (
                    <div className="table-container">
                        {getTemplateInstances(editingTemplate.id).length === 0 ? (
                            <div style={{ padding: 'var(--space-10)', textAlign: 'center', color: 'var(--neutral-500)' }}>
                                לא נמצאו קבוצות סקרים לתבנית זו
                            </div>
                        ) : (
                            <table className="data-table">
                                <thead>
                                    <tr>
                                        <th>שם הסקר</th>
                                        <th>ארגון</th>
                                        <th>סטטוס</th>
                                        <th>תאריך יצירה</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {getTemplateInstances(editingTemplate.id).map((instance) => (
                                        <tr key={instance.id}>
                                            <td style={{ fontWeight: 500 }}>{instance.name}</td>
                                            <td>{getOrgName(instance.organization_id)}</td>
                                            <td>
                                                <Badge variant={instance.is_active ? 'success' : 'neutral'}>
                                                    {instance.is_active ? 'פעיל' : 'לא פעיל'}
                                                </Badge>
                                            </td>
                                            <td style={{ color: 'var(--neutral-500)' }}>
                                                {new Date(instance.created_at).toLocaleDateString('he-IL')}
                                            </td>
                                            <td>
                                                <button
                                                    className="btn btn-icon btn-ghost sm"
                                                    onClick={() => handleCopyRegisterLink(instance.id)}
                                                    title="העתק קישור להרשמה"
                                                >
                                                    <Link size={16} />
                                                </button>
                                                {onNavigateToInstanceAnalytics && (
                                                    <button
                                                        className="btn btn-icon btn-ghost sm"
                                                        onClick={() => onNavigateToInstanceAnalytics(instance.id)}
                                                        title="ניתוח תוצאות"
                                                        style={{ color: 'var(--primary-400)' }}
                                                    >
                                                        <BarChart2 size={16} />
                                                    </button>
                                                )}
                                                <button
                                                    className="btn btn-icon btn-ghost sm"
                                                    onClick={() => {
                                                        setViewingInstance(instance);
                                                        setIsInstanceRespondersModalOpen(true);
                                                    }}
                                                    title="צפה במשיבים"
                                                >
                                                    <Users size={16} />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                )}
            </Modal>

            <RespondersModal
                isOpen={isRespondersModalOpen}
                onClose={() => setIsRespondersModalOpen(false)}
                title={`משיבים בתבנית: ${editingTemplate?.name}`}
                fetchResponders={() => getRespondersByTemplate(editingTemplate?.id || 0)}
                onNavigateToAnalytics={(respondentId) => onNavigateToRespondent?.(respondentId, { templateId: editingTemplate?.id })}
            />

            <RespondersModal
                isOpen={isInstanceRespondersModalOpen}
                onClose={() => setIsInstanceRespondersModalOpen(false)}
                title={`משיבים בסקר: ${viewingInstance?.name}`}
                fetchResponders={() => getRespondersByInstance(viewingInstance?.id || '')}
                onNavigateToAnalytics={(respondentId) => onNavigateToRespondent?.(respondentId, { instanceId: viewingInstance?.id, templateId: viewingInstance?.template_id })}
            />
        </div>
    );
};
